import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { media } from '../services/api';

const ProfileMediaEditor = ({ isOpen, onClose, type, currentImage, onSave }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [minZoom, setMinZoom] = useState(0.1);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load current image when modal opens
  useEffect(() => {
    if (isOpen && currentImage) {
      // Convert relative URL to full URL
      const fullImageUrl = currentImage.startsWith('http')
        ? currentImage
        : `https://api.bridalconnect.co.za${currentImage}`;
      setImageSrc(fullImageUrl);
    } else if (isOpen && !currentImage) {
      setImageSrc(null);
    }
  }, [isOpen, currentImage]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onMediaLoaded = useCallback((mediaSize) => {
    // Calculate minimum zoom to ensure image always covers the crop area
    const aspectRatio = type === 'logo' ? 1 : 16 / 4;
    const containerHeight = isMobile ? window.innerHeight * 0.5 : 400;
    const containerWidth = isMobile ? window.innerWidth * 0.9 : 600;

    // Calculate crop area dimensions based on container and aspect ratio
    let cropWidth, cropHeight;
    if (containerWidth / containerHeight > aspectRatio) {
      cropHeight = containerHeight * 0.8; // Leave some margin
      cropWidth = cropHeight * aspectRatio;
    } else {
      cropWidth = containerWidth * 0.8; // Leave some margin
      cropHeight = cropWidth / aspectRatio;
    }

    // Calculate minimum zoom needed to cover crop area
    const scaleX = cropWidth / mediaSize.naturalWidth;
    const scaleY = cropHeight / mediaSize.naturalHeight;
    const calculatedMinZoom = Math.max(scaleX, scaleY) * 0.8; // 0.8 to allow slight undersizing

    setMinZoom(Math.max(0.1, calculatedMinZoom));
  }, [type, isMobile]);

  const handleFileSelect = (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 }); // Reset crop position
      setZoom(1); // Reset zoom
      setError(null);
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    } else {
      setError('Please drop an image file');
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', reject);
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get the cropped image
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

      // Create form data
      const formData = new FormData();
      formData.append(type, croppedImage, `${type}.jpg`);

      // Upload with progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await media.upload(type, formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Success! Close modal and refresh
      setTimeout(() => {
        onSave();
        onClose();
        setImageSrc(null);
        setUploadProgress(0);
      }, 500);
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  const aspectRatio = type === 'logo' ? 1 : 16 / 4; // 1:1 for logo, 16:4 for banner

  return (
    <div style={styles.overlay(isMobile)} onClick={onClose}>
      <div
        style={styles.modal(isMobile)}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>
            {type === 'logo' ? 'Update Logo' : 'Update Banner'}
          </h2>
          <button style={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {!imageSrc ? (
          <div
            style={styles.uploadZone(dragActive)}
            onDrop={handleDrop}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
          >
            <div style={styles.uploadContent}>
              <div style={styles.uploadIcon}>📸</div>
              <h3>Drag & drop your image here</h3>
              <p>or</p>
              <label style={styles.fileButton}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                Choose from device
              </label>
              <div style={styles.hintContainer}>
                <p style={styles.hint}>
                  {type === 'logo'
                    ? 'Recommended logo size: 400x400px or larger (square format)'
                    : 'Recommended banner size: 1920x480px (4:1 ratio)'}
                </p>
                <p style={styles.subHint}>
                  {type === 'logo'
                    ? 'Your logo will be cropped to a circle. Use PNG format with transparent background for best results.'
                    : 'Banner images should be high quality and at least 1600px wide for crisp display on all devices.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={styles.cropperContainer(isMobile)}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onMediaLoaded={onMediaLoaded}
                cropShape={type === 'logo' ? 'round' : 'rect'}
                showGrid={false}
                minZoom={minZoom}
                maxZoom={5}
              />
            </div>

            <div style={styles.controls}>
              <div style={styles.zoomControl}>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Size</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Smaller</span>
                  <input
                    type="range"
                    min={minZoom}
                    max={5}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(e.target.value)}
                    style={styles.slider}
                  />
                  <span style={{ fontSize: '12px', color: '#666' }}>Larger</span>
                </div>
              </div>
            </div>
          </>
        )}

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {isUploading && (
          <div style={styles.progressBar}>
            <div style={styles.progressFill(uploadProgress)} />
          </div>
        )}

        {imageSrc && (
          <div style={styles.footer}>
            <label style={styles.replaceButton}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                style={{ display: 'none' }}
              />
              📁 Upload Different
            </label>
            <button
              style={styles.saveButton}
              onClick={handleSave}
              disabled={isUploading}
            >
              {isUploading ? `Uploading... ${uploadProgress}%` : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: (isMobile) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: isMobile ? 0 : '20px'
  }),
  modal: (isMobile) => ({
    backgroundColor: 'white',
    borderRadius: isMobile ? 0 : '12px',
    width: isMobile ? '100%' : '90%',
    maxWidth: isMobile ? '100%' : '600px',
    height: isMobile ? '100vh' : 'auto',
    maxHeight: isMobile ? '100vh' : '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }),
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '5px',
    width: '35px',
    height: '35px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background 0.2s',
    '&:hover': {
      backgroundColor: '#f0f0f0'
    }
  },
  uploadZone: (active) => ({
    margin: '20px',
    padding: '40px 20px',
    border: `2px dashed ${active ? '#007acc' : '#ccc'}`,
    borderRadius: '8px',
    backgroundColor: active ? '#f0f8ff' : '#fafafa',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  }),
  uploadContent: {
    pointerEvents: 'none'
  },
  uploadIcon: {
    fontSize: '48px',
    marginBottom: '10px'
  },
  fileButton: {
    display: 'inline-block',
    padding: '10px 24px',
    backgroundColor: '#007acc',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    marginTop: '10px',
    pointerEvents: 'all',
    transition: 'background 0.2s',
    '&:hover': {
      backgroundColor: '#005a9e'
    }
  },
  hintContainer: {
    marginTop: '15px',
    textAlign: 'center'
  },
  hint: {
    fontSize: '14px',
    color: '#333',
    margin: '0 0 8px 0',
    fontWeight: '500'
  },
  subHint: {
    fontSize: '12px',
    color: '#666',
    margin: '0',
    lineHeight: '1.4'
  },
  cropperContainer: (isMobile) => ({
    position: 'relative',
    width: '100%',
    height: isMobile ? '50vh' : '400px',
    backgroundColor: '#333'
  }),
  controls: {
    padding: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  zoomControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  slider: {
    flex: 1,
    height: '4px',
    outline: 'none',
    WebkitAppearance: 'none',
    backgroundColor: '#ddd',
    borderRadius: '2px'
  },
  footer: {
    display: 'flex',
    gap: '10px',
    padding: '20px',
    borderTop: '1px solid #e0e0e0',
    justifyContent: 'flex-end'
  },
  replaceButton: {
    display: 'inline-block',
    padding: '10px 20px',
    border: '1px solid #ccc',
    backgroundColor: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s',
    fontSize: '14px',
    color: '#333'
  },
  saveButton: {
    padding: '10px 20px',
    border: 'none',
    backgroundColor: '#007acc',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s'
  },
  error: {
    margin: '10px 20px',
    padding: '10px',
    backgroundColor: '#fee',
    color: '#c00',
    borderRadius: '4px',
    fontSize: '14px'
  },
  progressBar: {
    height: '4px',
    backgroundColor: '#e0e0e0',
    margin: '0 20px'
  },
  progressFill: (progress) => ({
    height: '100%',
    width: `${progress}%`,
    backgroundColor: '#007acc',
    transition: 'width 0.3s ease'
  })
};

export default ProfileMediaEditor;